import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Grid, Input as NativeInput, Text } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { getDailyCounts } from '@/api/order-controller';
import { SPACING } from '@/generic/config';
import BaseText from '@/base-components/Text';

// Dates are handled in UTC throughout: the backend stores scheduled_date as a
// pure DATE and the rest of the app treats a picked day as UTC-midnight (the old
// input used `new Date("yyyy-mm-dd")`). Using local getters/constructors here
// would shift days for non-UTC users (e.g. a Swedish user's midnight is the prior
// UTC day), which is exactly the off-by-one this avoids.
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const utcDay = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d));
const sameDay = (a: Date, b: Date) => ymd(a) === ymd(b);

const WEEKDAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const MONTHS = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli',
    'Augusti', 'September', 'Oktober', 'November', 'December'];

/**
 * Date picker for the Tasks page. Renders as a normal (read-only) input showing
 * the selected date; clicking it opens a month calendar popover where each day
 * shows a badge with how many CREATED tasks are scheduled that day, filtered by
 * the selected resource (or all). Counts come from /orders/overview/daily-counts.
 * Closes on selection, outside-click, or Escape.
 */
const TaskCalendar: React.FC<{
    label?: string;
    selectedDate: Date;
    onSelectDate: (d: Date) => void;
    resourceId: number | null;
}> = ({ label, selectedDate, onSelectDate, resourceId }) => {
    const [open, setOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(
        () => utcDay(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1));
    const containerRef = useRef<HTMLDivElement>(null);

    // Close on outside-click / Escape while open.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // 6-week (42 cell) grid, Monday-first, covering the visible month.
    const gridStart = useMemo(() => {
        const first = utcDay(viewMonth.getUTCFullYear(), viewMonth.getUTCMonth(), 1);
        const weekday = (first.getUTCDay() + 6) % 7; // Mon = 0
        return utcDay(first.getUTCFullYear(), first.getUTCMonth(), 1 - weekday);
    }, [viewMonth]);
    const days = useMemo(
        () => Array.from({ length: 42 }, (_, i) =>
            utcDay(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + i)),
        [gridStart]);

    // Only fetch counts while the popover is open.
    const { data: counts } = useQuery(
        ['dailyCounts', viewMonth.getFullYear(), viewMonth.getMonth(), resourceId],
        // Paused tasks are listed on the Tasks page too, so count them.
        () => getDailyCounts(days[0], days[41], resourceId, false),
        { enabled: open },
    );
    const countMap = useMemo(() => {
        const m: Record<string, number> = {};
        (counts ?? []).forEach((c) => { m[c.date] = c.count; });
        return m;
    }, [counts]);

    const now = new Date();
    const todayKey = ymd(utcDay(now.getFullYear(), now.getMonth(), now.getDate()));
    const shiftMonth = (delta: number) =>
        setViewMonth(utcDay(viewMonth.getUTCFullYear(), viewMonth.getUTCMonth() + delta, 1));

    const toggle = () => {
        if (!open) setViewMonth(utcDay(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1));
        setOpen((o) => !o);
    };
    const handleSelect = (d: Date) => {
        onSelectDate(d);
        setOpen(false);
    };

    return (
        <Box ref={containerRef} position="relative" textAlign="start">
            {label && (
                <BaseText weight="semibold" variant="normal" mb={SPACING / 4}>{label}</BaseText>
            )}
            <NativeInput
                mt={-SPACING / 2}
                value={ymd(selectedDate)}
                readOnly
                cursor="pointer"
                onClick={toggle}
            />

            {open && (
                <Box
                    position="absolute"
                    zIndex={20}
                    mt="4px"
                    w="320px"
                    maxW="90vw"
                    bg="white"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    p={SPACING / 2}
                    _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
                >
                    <Flex align="center" justify="space-between" mb={SPACING / 2}>
                        <Box as="button" onClick={() => shiftMonth(-1)} px={SPACING / 2} fontSize="lg"
                            cursor="pointer" aria-label="Föregående månad" _hover={{ color: 'blue.500' }}>‹</Box>
                        <Text fontWeight="semibold">{MONTHS[viewMonth.getUTCMonth()]} {viewMonth.getUTCFullYear()}</Text>
                        <Box as="button" onClick={() => shiftMonth(1)} px={SPACING / 2} fontSize="lg"
                            cursor="pointer" aria-label="Nästa månad" _hover={{ color: 'blue.500' }}>›</Box>
                    </Flex>
                    <Grid templateColumns="repeat(7, 1fr)" gap="2px">
                        {WEEKDAYS.map((w) => (
                            <Text key={w} textAlign="center" fontSize="xs" color="gray.500" fontWeight="600">{w}</Text>
                        ))}
                        {days.map((day) => {
                            const key = ymd(day);
                            const inMonth = day.getUTCMonth() === viewMonth.getUTCMonth();
                            const count = countMap[key] ?? 0;
                            const isSelected = sameDay(day, selectedDate);
                            const isToday = key === todayKey;
                            return (
                                <Box
                                    key={key}
                                    as="button"
                                    onClick={() => handleSelect(new Date(day.getTime()))}
                                    minH="40px"
                                    cursor="pointer"
                                    borderRadius="md"
                                    borderWidth={isToday && !isSelected ? '1px' : '0'}
                                    borderColor="blue.400"
                                    bg={isSelected ? 'blue.500' : count > 0 ? 'blue.50' : 'transparent'}
                                    color={isSelected ? 'white' : inMonth ? 'inherit' : 'gray.400'}
                                    _dark={{ bg: isSelected ? 'blue.500' : count > 0 ? 'blue.900' : 'transparent' }}
                                    _hover={{ bg: isSelected ? 'blue.600' : 'gray.100', _dark: { bg: 'gray.700' } }}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    transition="background 0.15s"
                                >
                                    <Text fontSize="sm" lineHeight="1">{day.getDate()}</Text>
                                    {count > 0 && (
                                        <Box mt="2px" px="6px" borderRadius="full" fontSize="10px" fontWeight="700"
                                            bg={isSelected ? 'whiteAlpha.500' : 'blue.500'} color="white">
                                            {count}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default TaskCalendar;
